
import { useEffect, useCallback } from "react";

interface UseExploreEffectsProps {
  categories: any[];
  initialCategoriesLoaded: React.MutableRefObject<boolean>;
  setCategoriesLoaded: (loaded: boolean) => void;
  categoryIdFromSlug: string | null;
  setSelectedCategory: (id: string | null) => void;
  searchQuery: string;
  selectedCategory: string | null;
  selectedSort: string;
  selectedTimeFilter: string;
  handleEnhancedSearchChange: (query: string) => void;
  handleSearchFilterChange: (filters: any) => void;
  isCategoryProcessing: React.MutableRefObject<boolean>;
  loadPrompts: () => void;
  loadDebounceTimer: React.MutableRefObject<NodeJS.Timeout | null>;
  lastDepsString: React.MutableRefObject<string>;
  initialLoadAttempted: React.MutableRefObject<boolean>;
  manualLoadTriggered: React.MutableRefObject<boolean>;
  currentPage: number;
  isInitialized: boolean;
  setIsInitialized: (initialized: boolean) => void;
  categoryChangeTimeout: React.MutableRefObject<NodeJS.Timeout | null>;
}

export function useExploreEffects({
  categories,
  initialCategoriesLoaded,
  setCategoriesLoaded,
  categoryIdFromSlug,
  setSelectedCategory,
  searchQuery,
  selectedCategory,
  selectedSort,
  selectedTimeFilter,
  handleEnhancedSearchChange,
  handleSearchFilterChange,
  isCategoryProcessing,
  loadPrompts,
  loadDebounceTimer,
  lastDepsString,
  initialLoadAttempted,
  manualLoadTriggered,
  currentPage,
  isInitialized,
  setIsInitialized,
  categoryChangeTimeout
}: UseExploreEffectsProps) {
  
  console.log('🎬 useExploreEffects: Running with state:', {
    categoriesCount: categories.length,
    categoryIdFromSlug,
    selectedCategory,
    searchQuery,
    isInitialized
  });

  // Update categoriesLoaded flag when categories are loaded
  useEffect(() => {
    if (initialCategoriesLoaded.current && categories.length > 0) {
      console.log("✅ useExploreEffects: Categories loaded successfully:", categories.length);
      setCategoriesLoaded(true);
    }
  }, [categories, initialCategoriesLoaded.current, setCategoriesLoaded]);

  // CRITICAL FIX: Set initial category when categoryIdFromSlug changes (from URL param)
  useEffect(() => {
    if (categoryIdFromSlug && initialCategoriesLoaded.current) {
      console.log(`🎯 useExploreEffects: CRITICAL - Setting initial category ID from URL: ${categoryIdFromSlug}`);
      console.log(`🎯 useExploreEffects: Current selectedCategory: ${selectedCategory}`);
      
      // Always set the category, even if it appears to be the same
      // This ensures the filtering logic gets triggered
      console.log(`🔄 useExploreEffects: FORCING category update from ${selectedCategory} to ${categoryIdFromSlug}`);
      setSelectedCategory(categoryIdFromSlug);
      
      // Mark that we need to process this category change
      isCategoryProcessing.current = true;
      
    } else if (categoryIdFromSlug === null && selectedCategory && initialCategoriesLoaded.current) {
      // Clear selection if URL param was cleared
      console.log("🧹 useExploreEffects: Clearing selected category as URL param was removed");
      setSelectedCategory(null);
    }
  }, [categoryIdFromSlug, setSelectedCategory, initialCategoriesLoaded.current]);

  // Sync enhanced search with current filters
  useEffect(() => {
    if (searchQuery) {
      console.log("🔍 useExploreEffects: Updating search with filters:", { 
        selectedSort, 
        selectedTimeFilter,
        selectedCategory
      });
      handleEnhancedSearchChange(searchQuery);
      handleSearchFilterChange({ 
        categoryFilter: selectedCategory || "",
        sortBy: selectedSort as any,
        timeFilter: selectedTimeFilter
      });
    }
  }, [searchQuery, selectedCategory, selectedSort, selectedTimeFilter, handleEnhancedSearchChange, handleSearchFilterChange]);

  // Simplified debounced loadPrompts function
  const debouncedLoadPrompts = useCallback(() => {
    // Don't load regular prompts if we have a search query
    if (searchQuery) {
      console.log("⏭️ useExploreEffects: Skipping prompt load - search query active");
      return;
    }
    
    // Clear previous timer
    if (loadDebounceTimer.current) {
      clearTimeout(loadDebounceTimer.current);
    }
    
    // Set new timer with reduced delay for faster loading
    loadDebounceTimer.current = setTimeout(() => {
      console.log("🚀 useExploreEffects: Executing debounced loadPrompts with:", {
        selectedCategory,
        selectedSort,
        selectedTimeFilter,
        currentPage
      });
      loadPrompts();
      
      // Clear the category processing flag after loading
      if (isCategoryProcessing.current) {
        console.log("✅ useExploreEffects: Clearing category processing flag after load");
        isCategoryProcessing.current = false;
      }
    }, 200); // Reduced delay for faster response when coming from action words
  }, [loadPrompts, searchQuery, selectedSort, selectedTimeFilter, loadDebounceTimer, selectedCategory, currentPage, isCategoryProcessing]);

  // Initial load trigger - improved timing
  useEffect(() => {
    if (initialCategoriesLoaded.current && !searchQuery && !initialLoadAttempted.current) {
      console.log("🎬 useExploreEffects: Triggering initial prompt load");
      initialLoadAttempted.current = true;
      
      // Load immediately for initial load
      setTimeout(() => {
        console.log("⚡ useExploreEffects: Executing initial load");
        loadPrompts();
      }, 100);
      
      if (!isInitialized) {
        setIsInitialized(true);
      }
    }
  }, [initialCategoriesLoaded.current, searchQuery, loadPrompts, initialLoadAttempted, isInitialized, setIsInitialized]);

  // Load prompts when dependencies change (only for non-search scenarios)
  useEffect(() => {
    // Skip if we have a search query (handled by enhanced search)
    if (searchQuery) {
      return;
    }
    
    // Only attempt to load prompts if categories have been loaded
    if (!initialCategoriesLoaded.current) {
      console.log("⏳ useExploreEffects: Waiting for categories to load");
      return;
    }
    
    // Create a dependency string to compare against the last one
    const depsString = `${selectedCategory}-${currentPage}-${selectedSort}-${selectedTimeFilter}`;
    
    // Trigger a reload if dependencies changed or if we're processing a category change
    if ((depsString !== lastDepsString.current && initialLoadAttempted.current) || isCategoryProcessing.current) {
      console.log(`🔄 useExploreEffects: Dependencies changed or category processing, triggering load:`, {
        from: lastDepsString.current,
        to: depsString,
        categoryProcessing: isCategoryProcessing.current
      });
      lastDepsString.current = depsString;
      debouncedLoadPrompts();
    }
  }, [selectedCategory, currentPage, selectedSort, selectedTimeFilter, searchQuery, initialCategoriesLoaded, isCategoryProcessing, lastDepsString, initialLoadAttempted, debouncedLoadPrompts]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (categoryChangeTimeout.current) {
        clearTimeout(categoryChangeTimeout.current);
      }
      
      if (loadDebounceTimer.current) {
        clearTimeout(loadDebounceTimer.current);
      }
    };
  }, [categoryChangeTimeout, loadDebounceTimer]);
}
